import {
  FileText,
  AlertTriangle,
  FileSearch,
  Clock,
  BarChart3,
  Users,
  Target,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Timer,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

const impactStats = [
  { value: "30 sec", label: "Avg. plan generation", icon: Timer },
  { value: "94%", label: "RTO prediction accuracy", icon: Target },
  { value: "12x", label: "Faster than manual BCM", icon: TrendingUp },
];

const beforeAfter = [
  { task: "Recovery Plan", before: "3-4 weeks", after: "30 seconds" },
  { task: "Business Impact Analysis", before: "2 weeks per dept", after: "1 hour" },
  { task: "Risk Assessment", before: "Spreadsheet chaos", after: "AI-scored instantly" },
  { task: "Compliance Gap Analysis", before: "External consultant", after: "Real-time dashboard" },
];

const capabilities = [
  {
    icon: FileText,
    title: "AI-Generated Recovery Plans",
    desc: "One click. Full BCP, IT-DRP, and crisis communication plan — tailored to your departments, risks, and RTO targets.",
    badge: "Auto-Generate",
    featured: true,
  },
  {
    icon: AlertTriangle,
    title: "Predictive Risk Scoring",
    desc: "AI identifies threats you haven't considered. Auto-scores likelihood and impact with treatment recommendations.",
    badge: "Predictive",
  },
  {
    icon: FileSearch,
    title: "Intelligent BIA Engine",
    desc: "Answer simple questions. AI calculates MTPD, RTO, RPO and maps process dependencies automatically.",
    badge: "AI-Driven",
  },
  {
    icon: Target,
    title: "5-Minute Maturity Assessment",
    desc: "AI benchmarks your BCM program against ISO 22301 and prioritizes the highest-impact quick wins.",
    badge: "Diagnostic",
  },
  {
    icon: BarChart3,
    title: "Live Compliance Dashboard",
    desc: "Real-time ISO 22301 gap analysis. See exactly what's missing, what's compliant, and what to fix next.",
    badge: "Real-Time",
  },
  {
    icon: Clock,
    title: "Auto-Building Recovery Timelines",
    desc: "Dependency maps and recovery sequences generate themselves as you enter data. Zero configuration.",
    badge: "Zero-Config",
  },
  {
    icon: Users,
    title: "Smart Task Assignment",
    desc: "AI suggests the right owner for each action item. Role-based workflows keep everyone focused.",
    badge: "Smart-Assign",
  },
  {
    icon: ShieldCheck,
    title: "Audit-Ready Evidence Collection",
    desc: "Reports compile automatically. Evidence collection runs on autopilot — you review and approve.",
    badge: "Auto-Evidence",
  },
];

export default function FeaturesSection() {
  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 border border-sky-200 text-sky-700 text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Intelligent Automation
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-5">
            Your AI-Powered{" "}
            <span className="text-sky-700">Resilience Engine</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Traditional BCM tools make you do the heavy lifting. ResiliencyHUB does it for you —
            AI handles the complexity while you focus on decisions that matter.
          </p>
        </div>

        {/* Impact Stats Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 mb-16 py-6 border-y border-slate-200">
          {impactStats.map((stat, i) => (
            <div key={i} className="flex items-center gap-3 text-center sm:text-left">
              <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                <stat.icon className="w-5 h-5 text-sky-700" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900 tracking-tight">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Before/After Comparison */}
        <div className="mb-16">
          <h3 className="text-lg font-bold text-slate-900 mb-6 text-center">What Used to Take Weeks Now Takes Minutes</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {beforeAfter.map((item, i) => (
              <div key={i} className="bg-zinc-50 border border-slate-200 rounded-xl p-5 text-center hover:border-sky-300 hover:shadow-md transition-all duration-300">
                <p className="text-sm font-semibold text-slate-900 mb-3">{item.task}</p>
                <div className="flex items-center justify-center gap-3">
                  <div>
                    <p className="text-xs text-slate-400 mb-0.5">Before</p>
                    <p className="text-sm font-bold text-red-500 line-through decoration-red-400/50">{item.before}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                  <div>
                    <p className="text-xs text-slate-400 mb-0.5">With ResiliencyHUB</p>
                    <p className="text-sm font-bold text-emerald-600">{item.after}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bento Capabilities Grid */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Featured Card — spans 2 rows */}
          <div
            data-testid="ai-feature-card-0"
            className="lg:row-span-2 bg-slate-900 rounded-2xl p-8 flex flex-col justify-between hover:shadow-xl transition-all duration-300"
          >
            <div>
              <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-sky-500/15 text-sky-400 border border-sky-500/20 mb-5">
                {capabilities[0].badge}
              </div>
              <div className="w-14 h-14 bg-sky-500/15 rounded-2xl flex items-center justify-center mb-5 ring-4 ring-sky-500/10">
                <FileText className="w-7 h-7 text-sky-400" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3">{capabilities[0].title}</h4>
              <p className="text-base text-slate-400 leading-relaxed mb-6">{capabilities[0].desc}</p>
            </div>
            <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700/50">
              <p className="text-xs text-slate-500 mb-2">What gets generated:</p>
              <div className="space-y-2">
                {["Business Continuity Plan (BCP)", "IT Disaster Recovery Plan", "Crisis Communication Plan", "Recovery Procedures per department"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Regular Cards — light cards */}
          {capabilities.slice(1).map((cap, i) => (
            <div
              key={i}
              data-testid={`ai-feature-card-${i + 1}`}
              className="bg-zinc-50 border border-slate-200 rounded-2xl p-6 hover:border-sky-300 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 bg-sky-100 rounded-xl flex items-center justify-center">
                  <cap.icon className="w-5 h-5 text-sky-700" />
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-100 text-emerald-700 border border-emerald-200">
                  {cap.badge}
                </span>
              </div>
              <h4 className="text-base font-bold text-slate-900 mb-2">{cap.title}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{cap.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom Tagline */}
        <div className="mt-14 text-center">
          <div className="inline-flex items-center gap-4 px-6 py-3 bg-slate-900 rounded-full">
            <span className="text-sky-400 font-medium text-sm">AI does the work</span>
            <span className="w-1 h-1 bg-slate-600 rounded-full" />
            <span className="text-emerald-400 font-medium text-sm">You stay in control</span>
          </div>
        </div>
      </div>
    </section>
  );
}
